// Developer: Ankit Gupta
/** Typed client for the Credits API. */

import apiClient, { api } from '@/api/client';
import type {
  AccountPage,
  Balance,
  BillingSettings,
  Earnings,
  Payment,
  Plan,
  PlanInput,
  GeneratedKeys,
  GenerateKeysInput,
  LedgerPage,
  Price,
  PriceInput,
  RedeemResponse,
  TopupKeyPage,
} from './types';

const BASE = '/credits';
const ADMIN = '/admin/credits';

/** This user's balance, totals and most recent movements. */
export const getBalance = () => api.get<Balance>(`${BASE}/me`);

/** Paged credit history for this user. */
export const getLedger = (params: {
  page?: number;
  page_size?: number;
  entry_type?: string;
}) => api.get<LedgerPage>(`${BASE}/ledger`, params);

/** Redeem a top-up key. */
export const redeemKey = (key: string) =>
  api.post<RedeemResponse>(`${BASE}/redeem`, { key });

// --- Admin -----------------------------------------------------------------

/** Mint keys. The plaintexts come back once and are not recoverable. */
export const generateKeys = (body: GenerateKeysInput) =>
  api.post<GeneratedKeys>(`${ADMIN}/keys`, body);

/** List issued keys and their state (never their plaintext). */
export const listKeys = (params: {
  page?: number;
  page_size?: number;
  status?: string;
  batch_ref?: string;
}) => api.get<TopupKeyPage>(`${ADMIN}/keys`, params);

/** Take an unredeemed key out of circulation. */
export const revokeKey = (id: number) => api.post(`${ADMIN}/keys/${id}/revoke`, {});

/** List billing accounts. */
export const listAccounts = (params: {
  page?: number;
  page_size?: number;
  search?: string;
}) => api.get<AccountPage>(`${ADMIN}/accounts`, params);

/** Grant or remove credits by hand. Always recorded, always with a reason. */
export const adjustAccount = (id: number, credits: number, note: string) =>
  api.post<{ balance_credits: number }>(`${ADMIN}/accounts/${id}/adjust`, {
    credits,
    note,
  });

/** The pricing catalogue. */
export const listPrices = () => api.get<Price[]>(`${ADMIN}/prices`);

/** Change what an operation costs. Takes effect on the next call. */
export const updatePrice = (id: number, body: PriceInput) =>
  api.put<Price>(`${ADMIN}/prices/${id}`, body);

// --- What a credit is worth, and plans --------------------------------------

/** What one credit is worth in US dollars. */
export const getRate = () => api.get<{ credit_value_usd: number }>(`${BASE}/rate`);

/** The plans an account can be on. */
export const getPlans = () => api.get<Plan[]>(`${BASE}/plans`);

export const getBillingSettings = () => api.get<BillingSettings>(`${ADMIN}/settings`);

/** Takes effect on the next request. Balances stay in credits; their dollar worth moves. */
export const updateBillingSettings = (body: Omit<BillingSettings, 'credits_per_runtime_dollar' | 'updated_by' | 'default_plan_code'> & { default_plan_code?: string }) =>
  api.put<BillingSettings>(`${ADMIN}/settings`, body);

export const listPlans = () => api.get<Plan[]>(`${ADMIN}/plans`);
export const createPlan = (body: PlanInput) => api.post<Plan>(`${ADMIN}/plans`, body);
export const updatePlan = (id: number, body: PlanInput) => api.put<Plan>(`${ADMIN}/plans/${id}`, body);

/** Put an account on a plan (null takes it off). The month's allowance is granted at once. */
export const setAccountPlan = (accountId: number, planCode: string | null) =>
  api.put<{ plan_code: string | null; granted_credits: number; balance_credits: number }>(
    `${ADMIN}/accounts/${accountId}/plan`,
    { plan_code: planCode },
  );

/** Grant this month's allowance to every account that is due. */
export const renewPlans = () =>
  api.post<{ accounts: number; granted: number; credits: number }>(`${ADMIN}/plans/renew`, {});

// --- Payments and earnings ---------------------------------------------------

/** Record money a customer paid. It moves no credits; it is what "earned" is counted from. */
export const recordPayment = (body: { account_id: number; kind: Payment['kind']; period: string; amount_usd: number; reference?: string; note?: string }) =>
  api.post<Payment>(`${ADMIN}/payments`, body);
export const listPayments = (params: { account_id?: number; period?: string }) => api.get<Payment[]>(`${ADMIN}/payments`, params);
export const voidPayment = (id: number) => api.post<Payment>(`${ADMIN}/payments/${id}/void`, {});
/** Set, or clear, what was paid for a top-up key. */
export const setKeyAmount = (id: number, amount: number | null) => api.put(`${ADMIN}/keys/${id}/amount`, { amount_paid_usd: amount });

export const getEarnings = (months: number) => api.get<Earnings>(`${ADMIN}/earnings`, { months });
export const getEarningsCsv = (months: number) =>
  apiClient.get(`${ADMIN}/earnings`, { params: { months, format: 'csv' }, responseType: 'blob' }).then((r) => r.data as Blob);
